/* ==========================================================================
   AUMI — facets.js
   <facets-root>: filtros e ordenação sem recarregar a página (Section Rendering),
   gaveta de filtros no celular, "Carregar mais" e histórico do navegador.
   Sem JS tudo continua funcionando: links de filtro, <noscript> e paginação.
   ========================================================================== */
(() => {
  if (customElements.get('facets-root')) return;

  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };
  const withSection = (url, sectionId) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    return target.toString();
  };

  class FacetsRoot extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.onPopState = () => this.render(window.location.href, false);

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-facet-price]')) return;
        if (event.target.closest('[data-facets-form]') || event.target.matches('[data-facets-sort]')) this.submit();
      });
      this.addEventListener('input', debounce((event) => {
        if (event.target.matches('[data-facet-price]')) this.submit();
      }, 700));
      this.addEventListener('submit', (event) => {
        if (!event.target.matches('[data-facets-form]')) return;
        event.preventDefault();
        this.submit();
      });
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-drawer-open')) this.closeDrawer();
      });
      window.addEventListener('popstate', this.onPopState);

      /* horizontal: fecha os outros dropdowns ao abrir um */
      this.addEventListener(
        'toggle',
        (event) => {
          const details = event.target;
          if (!details.matches || !details.matches('.facets--horizontal [data-facet]') || !details.open) return;
          if (window.matchMedia('(max-width: 989px)').matches) return;
          this.querySelectorAll('.facets--horizontal [data-facet][open]').forEach((other) => {
            if (other !== details) other.open = false;
          });
        },
        true
      );
      this.outside = (event) => {
        if (window.matchMedia('(max-width: 989px)').matches) return;
        this.querySelectorAll('.facets--horizontal [data-facet][open]').forEach((details) => {
          if (!details.contains(event.target)) details.open = false;
        });
      };
      document.addEventListener('click', this.outside);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      document.removeEventListener('click', this.outside);
      document.documentElement.classList.remove('no-scroll');
    }

    onClick(event) {
      if (event.target.closest('[data-facets-open]')) {
        this.openDrawer();
        return;
      }
      if (event.target.closest('[data-facets-close]')) {
        this.closeDrawer();
        return;
      }
      const link = event.target.closest('a[data-facet-link]');
      if (link) {
        event.preventDefault();
        this.render(link.href);
        return;
      }
      const more = event.target.closest('a[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
      }
    }

    get panel() {
      return this.querySelector('[data-facets-panel]');
    }

    isDrawer() {
      const panel = this.panel;
      return panel && (window.matchMedia('(max-width: 989px)').matches || panel.classList.contains('facets--drawer'));
    }

    openDrawer() {
      if (!this.panel) return;
      this.lastFocus = document.activeElement;
      this.classList.add('is-drawer-open');
      this.querySelector('.facets-overlay')?.classList.add('is-open');
      this.querySelectorAll('[data-facets-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      document.documentElement.classList.add('no-scroll');
      this.panel.querySelector('[data-facets-close]')?.focus();
    }

    closeDrawer() {
      this.classList.remove('is-drawer-open');
      this.querySelector('.facets-overlay')?.classList.remove('is-open');
      this.querySelectorAll('[data-facets-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      document.documentElement.classList.remove('no-scroll');
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
    }

    buildUrl() {
      const form = this.querySelector('[data-facets-form]');
      const params = new URLSearchParams();
      if (form) {
        new FormData(form).forEach((value, key) => {
          if (value !== '') params.append(key, value);
        });
      }
      const sort = this.querySelector('[data-facets-sort]');
      if (sort && !params.has('sort_by')) params.set('sort_by', sort.value);
      const terms = this.dataset.terms;
      if (terms && !params.has('q')) params.set('q', terms);
      const query = params.toString();
      return `${window.location.pathname}${query ? `?${query}` : ''}`;
    }

    submit() {
      this.render(this.buildUrl());
    }

    async fetchHtml(url) {
      const key = withSection(url, this.sectionId);
      if (this.cache.has(key)) return this.cache.get(key);
      const html = await fetch(key).then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      });
      this.cache.set(key, html);
      return html;
    }

    async render(url, push = true) {
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      const controller = (this.controller = {});
      try {
        const html = await this.fetchHtml(url);
        if (controller !== this.controller) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const active = document.activeElement;
        this.querySelectorAll('[data-facet-region]').forEach((region) => {
          const fresh = doc.querySelector(`[data-facet-region="${region.dataset.facetRegion}"]`);
          if (!fresh) return;
          if (region.contains(active) && active.matches('input[type="number"], input[type="text"]')) return;
          const focusedId = region.contains(active) ? active.id : null;
          region.innerHTML = fresh.innerHTML;
          region.hidden = fresh.hidden;
          if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
        });
        if (push) window.history.pushState({ facets: true }, '', url);
        if (window.AUMI && typeof window.AUMI.reveal === 'function') window.AUMI.reveal(this);
      } catch (error) {
        window.location.href = url;
      } finally {
        if (controller === this.controller) {
          this.classList.remove('is-loading');
          this.removeAttribute('aria-busy');
        }
      }
    }

    async loadMore(link) {
      if (link.classList.contains('is-loading')) return;
      link.classList.add('is-loading');
      link.setAttribute('aria-busy', 'true');
      try {
        const html = await this.fetchHtml(link.href);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = this.querySelector('[data-results-grid]');
        const freshItems = doc.querySelectorAll('[data-results-grid] > *');
        const firstNew = freshItems[0];
        freshItems.forEach((item) => grid.append(document.importNode(item, true)));
        const wrap = this.querySelector('[data-pagination]');
        const freshWrap = doc.querySelector('[data-pagination]');
        if (wrap) wrap.innerHTML = freshWrap ? freshWrap.innerHTML : '';
        if (firstNew) {
          const appended = grid.children[grid.children.length - freshItems.length];
          appended?.querySelector('a[href]')?.focus({ preventScroll: true });
        }
        if (window.AUMI && typeof window.AUMI.reveal === 'function') window.AUMI.reveal(grid);
      } catch (error) {
        window.location.href = link.href;
      }
    }
  }

  customElements.define('facets-root', FacetsRoot);
})();
